/**
 * OnboardingStepCard — the one card an onboarding step lives in.
 *
 * Each step used to draw its own: `glass rounded-xl shadow-sm p-6` here,
 * `p-8` there, an `<h2 className="text-xl font-bold">` in one file and
 * `text-2xl font-bold` in the next, and three different content widths
 * (`max-w-2xl` cards inside a `max-w-3xl` column under a `max-w-4xl` rail),
 * so the panel changed size and its heading changed weight as the user moved
 * through the flow.
 *
 * This is FormShell's header band — `text-lg text-fg-strong` over
 * `text-sm text-fg-muted`, separated by a hairline — on a `GlassCard`-shaped
 * surface, at ONE width. Onboarding cannot use `FormShell` itself: the shell
 * is a full-height layout with a pinned `glass-bar` footer, and these steps
 * are content-sized cards centred in a scrolling page.
 *
 * The footer slot takes a `FormActions` row so every step's primary button
 * lands in the same place.
 */

import React from 'react';
import clsx from 'clsx';

/** Every onboarding surface — rail, cards, error row — is this wide. */
export const ONBOARDING_MAX_WIDTH = 'max-w-2xl';

export interface OnboardingStepCardProps {
  /** Already-translated headline. */
  title: string;
  /** Already-translated line under the title. */
  description?: string;
  /** Centred glyph above the title — the welcome step's logo mark. */
  media?: React.ReactNode;
  /** Centres the header text under `media`. */
  align?: 'start' | 'center';
  /** Action row, normally a `<FormActions>`. */
  footer?: React.ReactNode;
  /** Root element — `form` when the card wraps a submitting form. */
  as?: 'div' | 'form';
  onSubmit?: React.FormEventHandler<HTMLFormElement>;
  className?: string;
  children?: React.ReactNode;
}

const OnboardingStepCard: React.FC<OnboardingStepCardProps> = ({
  title,
  description,
  media,
  align = 'start',
  footer,
  as = 'div',
  onSubmit,
  className,
  children,
}) => {
  const centered = align === 'center';

  const body = (
    <>
      <div className={clsx('px-6 pt-6 pb-4 border-b border-hairline', centered && 'text-center')}>
        {media && <div className="mb-4 flex justify-center">{media}</div>}
        <h2 className="text-lg text-fg-strong mb-1">{title}</h2>
        {description && <p className="text-sm text-fg-muted">{description}</p>}
      </div>

      {children && <div className="px-6 py-6 space-y-4">{children}</div>}

      {footer && (
        <div className={clsx('px-6 pb-6', !children && 'pt-6')}>{footer}</div>
      )}
    </>
  );

  const rootClass = clsx(
    'glass rounded-lg shadow-sm w-full mx-auto',
    ONBOARDING_MAX_WIDTH,
    className
  );

  if (as === 'form') {
    return (
      <form onSubmit={onSubmit} className={rootClass}>
        {body}
      </form>
    );
  }

  return <div className={rootClass}>{body}</div>;
};

export default OnboardingStepCard;
